import { HugeiconsIcon } from "@hugeicons/react";
import { useState } from "react";
import { FieldArray, useFormikContext, getIn } from "formik";
import * as Yup from "yup";
import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, FormGroup, IconButton, Paper, Radio, RadioGroup, Stack, Switch, Tooltip, Typography } from "@mui/material";
import { v4 } from "uuid";
import InputText from "@/components/InputText";
import { Content, Setting, SettingType } from "@/types";
import FeatureLock from "@/components/FeatureLock";
import useOrganizationPlan from "@/hooks/useOrganizationPlan";
import { Add01Icon, ArrowTurnBackwardIcon, Delete02Icon, SettingDone01Icon } from "@hugeicons/core-free-icons";
import Empty from "./components/Empty";

const EMPTY_SETTING = {
  title: "",
  price: null,
  isAvailable: true,
  type: SettingType.OPTIONS,
};

const EMPTY_OPTION = {
  title: "",
  price: 0,
  type: "option",
};

const EMPTY_SUBTITLE = {
  title: "",
  type: "subtitle",
};

export const SettingsSchema = Yup.object().shape({
  settings: Yup.array().of(
    Yup.object().shape({
      title: Yup.string(),
      options: Yup.array().of(
        Yup.object().shape({
          title: Yup.string().min(4, "Muy Corto!").max(50, "Muy Largo!").required("Campo requerido"),
          price: Yup.number(),
        }),
      ),
    }),
  ),
});

const Settings = () => {
  const { isFeatureAvailable } = useOrganizationPlan("content");
  const { values, errors, touched, setFieldValue } = useFormikContext<Content>();
  const [type] = useState<SettingType>(SettingType.OPTIONS ?? "");

  if (!values.settings || !Array.isArray(values.settings)) {
    return null;
  }

  if (!isFeatureAvailable("settings")) {
    return (
      <FeatureLock
        icon={SettingDone01Icon}
        title="Simplifica tu Menú con Variantes Inteligentes"
        description="Evita duplicar platos innecesariamente. Con el Plan Pro, permite que tus clientes elijan tamaños, términos de cocción e ingredientes en un solo lugar, con actualización de precios en tiempo real. ¡Haz tu menú más profesional y fácil de navegar!"
      />
    );
  }

  return (
    <Stack spacing={2}>
      <FieldArray name="settings">
        {({ push, replace }) => {
          const addOption = (si: number, type: "option" | "subtitle" = "option") => {
            const settings = (values?.settings ?? []) as Setting[];

            if (!settings?.[si]) return;

            if (settings[si]?.options) {
              replace(si, {
                ...settings[si],
                options: [
                  ...settings[si].options,
                  {
                    ...(type === "option" ? EMPTY_OPTION : EMPTY_SUBTITLE),
                    id: v4(),
                  },
                ],
              });
            } else {
              replace(si, {
                ...settings[si],
                options: [
                  {
                    ...(type === "option" ? EMPTY_OPTION : EMPTY_SUBTITLE),
                    id: v4(),
                  },
                ],
              });
            }
          };

          const hasSettings = values.settings && values.settings.length > 0;

          if (!hasSettings) {
            return (
              <Empty onCreate={() => push(EMPTY_SETTING)} />
            );
          }

          return (
            <Stack spacing={2}>
              {((values?.settings as Setting[]) ?? []).map((s, si) => {
                const isAvailable = values.settings?.[si]?.isAvailable ?? false;
                return (
                  <Paper
                    key={`${s}-${si}`}
                    elevation={2}
                    sx={{
                      padding: 2,
                      borderRadius: 2,
                    }}
                  >
                    {s?.softDelete ? (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h4" sx={{ textDecoration: "line-through" }}>
                          {s?.title ?? ""}
                        </Typography>
                        <Tooltip arrow placement="top" title="Restaurar Configuración">
                          <IconButton
                            onClick={() => {
                              replace(si, {
                                ...s,
                                softDelete: false,
                              });
                            }}
                          >
                            <HugeiconsIcon icon={ArrowTurnBackwardIcon} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Stack spacing={2}>
                        {values.price <= 0 && !isAvailable ? <Alert severity="error">El precio de tu plato es 0, asegurate de cambiarlo para evitar errores.</Alert> : null}
                        <Stack direction={"row"} spacing={2} alignItems={"center"}>
                          <Tooltip title={isAvailable ? "Desactivar" : "Activar"} arrow placement="top">
                            <Switch
                              name={`settings[${si}].isAvailable`}
                              size="medium"
                              checked={isAvailable}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFieldValue(`settings.${si}.isAvailable`, checked);

                                if (!checked) {
                                  setFieldValue(`settings.${si}.defaultOption`, null);
                                }
                              }}
                            />
                          </Tooltip>
                          <InputText
                            label="Título"
                            name={`settings[${si}].title`}
                            size="small"
                            value={(values.settings as Setting[])[si]?.title ?? ""}
                            error={Boolean(getIn(touched, `settings.${si}.title`) && getIn(errors, `settings.${si}.title`))}
                            helperText={getIn(touched, `settings.${si}.title`) && getIn(errors, `settings.${si}.title`)}
                          />

                          <Tooltip title="Eliminar Configuración" arrow placement="top">
                            <IconButton
                              color="error"
                              onClick={() => {
                                replace(si, {
                                  ...s,
                                  softDelete: true,
                                });
                              }}
                            >
                              <HugeiconsIcon icon={Delete02Icon} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Stack direction={"row"} spacing={2}>
                          {/* MIN-MAX */}
                          <Stack direction={"row"} spacing={2} flex={1} paddingLeft={9} paddingRight={7}>
                            <InputText
                              label="Mínimo"
                              type="number"
                              name={`settings[${si}].min`}
                              size="small"
                              value={Number((values?.settings as Setting[])[si]?.min) ?? 1}
                              error={Boolean(getIn(touched, `settings.${si}.min`) && getIn(errors, `settings.${si}.min`))}
                              helperText={getIn(touched, `settings.${si}.min`) && getIn(errors, `settings.${si}.min`)}
                            />
                            <InputText
                              label="Máximo"
                              type="number"
                              name={`settings[${si}].max`}
                              size="small"
                              value={Number((values?.settings as Setting[])[si].max) ?? 1}
                              error={Boolean(getIn(touched, `settings.${si}.max`) && getIn(errors, `settings.${si}.max`))}
                              helperText={getIn(touched, `settings.${si}.max`) && getIn(errors, `settings.${si}.max`)}
                              onChange={(e) => {
                                const name = e.target.name;
                                const value = e.target.value;

                                if (Number(value) >= 1) {
                                  setFieldValue(name, value);
                                }
                              }}
                            />
                          </Stack>

                          {/* <Stack direction={'row'} spacing={2} flex={1}>
                            <RadioGroup
                              value={type}
                              onChange={handleChangeType}
                              sx={{
                                flexDirection: 'row',
                                justifyContent: 'space-around',
                              }}
                            >
                              <FormControlLabel
                                label="Opciones"
                                value={SettingType.OPTIONS}
                                control={<Radio size="small" color="primary" />}
                              />
                              <FormControlLabel
                                label="Número"
                                value={SettingType.NUMBER}
                                control={<Radio size="small" color="primary" />}
                                disabled
                              />
                            </RadioGroup>
                          </Stack> */}
                        </Stack>

                        {type === SettingType.OPTIONS ? (
                          <Stack spacing={2}>
                            <Divider
                              sx={{
                                fontSize: "0.80rem",
                                textTransform: "uppercase",
                                color: "GrayText",
                              }}
                            >
                              Opciones
                            </Divider>

                            {s.options?.map((o: any, oi: number) => (
                              <Stack spacing={2} key={`${o}-${oi}`} direction="row">
                                {Number(s.max) === 1 ? (
                                  <RadioGroup
                                    value={`default-option-${si}.defaultOption`}
                                    onChange={() => {
                                      setFieldValue(`settings.${si}.defaultOption`, o.id);
                                    }}
                                  >
                                    <Radio
                                      checked={(values.settings as Setting[])[si].defaultOption === o.id}
                                      disabled={o?.type === "subtitle"}
                                      sx={{
                                        opacity: o?.type === "subtitle" ? 0 : 1,
                                      }}
                                    />
                                  </RadioGroup>
                                ) : (
                                  <FormGroup>
                                    <Checkbox
                                      checked={(values.settings as Setting[])[si]?.defaultOption?.includes(o.id) ?? false}
                                      disabled={
                                        o?.type === "subtitle"
                                          ? true
                                          : ((values.settings as Setting[])[si]?.defaultOption?.length ?? 0) >= (s?.max ?? 0) &&
                                            !(values.settings as Setting[])[si]?.defaultOption?.includes(o.id)
                                      }
                                      onChange={() => {
                                        const setting = (values.settings as Setting[])[si];
                                        const defaultOptionArray = Array.isArray(setting?.defaultOption) ? setting.defaultOption : [];
                                        const newDefaultOption = defaultOptionArray.includes(o.id)
                                          ? defaultOptionArray.filter((id: string) => id !== o.id)
                                          : [...defaultOptionArray, o.id];
                                        setFieldValue(`settings.${si}.defaultOption`, newDefaultOption);
                                      }}
                                      sx={{
                                        opacity: o?.type === "subtitle" ? 0 : 1,
                                      }}
                                    />
                                  </FormGroup>
                                )}
                                <InputText
                                  label={`Opción ${oi + 1}`}
                                  name={`settings[${si}].options.${oi}.title`}
                                  value={(values.settings as Setting[])[si].options?.[oi].title ?? ""}
                                  size="small"
                                  error={Boolean(getIn(touched, `settings.${si}.options.${oi}.title`) && getIn(errors, `settings.${si}.options.${oi}.title`))}
                                  helperText={getIn(touched, `settings.${si}.options.${oi}.title`) && getIn(errors, `settings.${si}.options.${oi}.title`)}
                                />
                                <Stack flex={1} direction={"row"} spacing={2}>
                                  {o.type !== "subtitle" ? (
                                    <InputText
                                      label="Precio"
                                      name={`settings[${si}].options.${oi}.price`}
                                      value={(values.settings as Setting[])[si].options?.[oi].price ?? ""}
                                      size="small"
                                      sx={{ width: 100 }}
                                      error={Boolean(getIn(touched, `settings.${si}.options.${oi}.price`) && getIn(errors, `settings.${si}.options.${oi}.price`))}
                                      helperText={getIn(touched, `settings.${si}.options.${oi}.price`) && getIn(errors, `settings.${si}.options.${oi}.price`)}
                                    />
                                  ) : null}

                                  <Tooltip title="Eliminar Opción" arrow placement="top">
                                    <IconButton
                                      color="error"
                                      onClick={() => {
                                        const setting = {
                                          ...(values.settings as Setting[])[si],
                                        };
                                        const options = setting?.options?.filter((_, index) => index !== oi);
                                        replace(si, {
                                          ...setting,
                                          options,
                                        });
                                      }}
                                    >
                                      <HugeiconsIcon icon={Delete02Icon} />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                                <Divider />
                              </Stack>
                            ))}
                            <Stack direction="row" spacing={2}>
                              <Button variant="text" startIcon={<HugeiconsIcon icon={Add01Icon} />} onClick={() => addOption(si)}>
                                Opción
                              </Button>
                              <Button variant="text" startIcon={<HugeiconsIcon icon={Add01Icon} />} onClick={() => addOption(si, "subtitle")}>
                                Subtítulo
                              </Button>
                            </Stack>
                          </Stack>
                        ) : null}
                      </Stack>
                    )}
                  </Paper>
                );
              })}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Button variant="outlined" startIcon={<HugeiconsIcon icon={Add01Icon} />} onClick={() => push(EMPTY_SETTING)}>
                  Agregar Configuración
                </Button>
              </Box>
            </Stack>
          );
        }}
      </FieldArray>
    </Stack>
  );
};

export default Settings;
